import type { JobDetail, JobSummary, SearchResponse } from "@/lib/types";

const now = Math.floor(Date.now() / 1000);

export const demoJobs: JobSummary[] = [
  {
    job_id: "demo-product-designer",
    normalized_title: "Senior Product Designer",
    title: "Senior Product Designer",
    company: "Kite Labs",
    company_slug: "kite-labs",
    location: "Bengaluru, Karnataka",
    city: "Bengaluru",
    work_model: "Hybrid",
    employment_type: "Full-time",
    experience_level: "Senior",
    job_description: "Shape simple, thoughtful financial products used by growing businesses across India.",
    last_seen_date: now,
    created_at: now - 86_400,
  },
  {
    job_id: "demo-frontend-engineer",
    normalized_title: "Frontend Engineer",
    title: "Frontend Engineer II",
    company: "Northstar Health",
    company_slug: "northstar-health",
    location: "Remote · India",
    city: "Remote",
    work_model: "Remote",
    employment_type: "Full-time",
    experience_level: "Mid-level",
    job_description: "Build fast, accessible care experiences with React, TypeScript, and a modern design system.",
    last_seen_date: now - 86_400,
    created_at: now - 172_800,
    is_remote: true,
  },
  {
    job_id: "demo-growth-lead",
    normalized_title: "Growth Marketing Lead",
    title: "Growth Marketing Lead",
    company: "Mango Commerce",
    company_slug: "mango-commerce",
    location: "Mumbai, Maharashtra",
    city: "Mumbai",
    work_model: "On-site",
    employment_type: "Full-time",
    experience_level: "Lead",
    job_description: "Own the growth engine for a commerce platform serving independent brands across the country.",
    last_seen_date: now,
    created_at: now - 259_200,
  },
  {
    job_id: "demo-data-analyst",
    normalized_title: "Data Analyst",
    title: "Data Analyst — Operations",
    company: "ParcelOne",
    company_slug: "parcelone",
    location: "Gurugram, Haryana",
    city: "Gurugram",
    work_model: "Hybrid",
    employment_type: "Full-time",
    experience_level: "Mid-level",
    job_description: "Turn logistics data into clear decisions for one of India's fastest-growing delivery networks.",
    last_seen_date: now - 172_800,
    created_at: now - 259_200,
  },
  {
    job_id: "demo-people-partner",
    normalized_title: "People Operations Partner",
    title: "People Operations Partner",
    company: "Common Ground",
    company_slug: "common-ground",
    location: "Pune, Maharashtra",
    city: "Pune",
    work_model: "Hybrid",
    employment_type: "Full-time",
    experience_level: "Mid-level",
    job_description: "Design the systems and rituals that help a distributed technology team do its best work.",
    last_seen_date: now,
    created_at: now - 345_600,
  },
  {
    job_id: "demo-ai-engineer",
    normalized_title: "Applied AI Engineer",
    title: "Applied AI Engineer",
    company: "Saffron Systems",
    company_slug: "saffron-systems",
    location: "Hyderabad, Telangana",
    city: "Hyderabad",
    work_model: "On-site",
    employment_type: "Full-time",
    experience_level: "Senior",
    job_description: "Take language and vision models from prototype to reliable production workflows.",
    last_seen_date: now - 86_400,
    created_at: now - 432_000,
  },
];

export const demoDetails: Record<string, JobDetail> = Object.fromEntries(
  demoJobs.map((job) => [
    job.job_id,
    {
      ...job,
      description: job.job_description,
      responsibilities: [
        "Own meaningful projects from early discovery through launch",
        "Work closely with product, engineering, and business partners",
        "Use evidence and clear communication to improve the final outcome",
      ],
      minimum_qualifications: [
        "Demonstrated experience delivering high-quality work in a related role",
        "Strong communication and collaborative problem-solving skills",
        "Comfort operating with ownership in a fast-moving environment",
      ],
      preferred_qualifications: ["Experience working with teams or customers across India"],
      skills_required: ["Communication", "Problem solving", "Collaboration", "Ownership"],
      benefits: ["Flexible leave", "Learning budget", "Health insurance"],
      education_level: "Degree or equivalent practical experience",
    },
  ]),
);

export function demoSearch(query = "", location = "", limit = 10, offset = 0): SearchResponse {
  const normalizedQuery = query.toLowerCase();
  const normalizedLocation = location.toLowerCase();
  const filtered = demoJobs.filter((job) => {
    const searchable = `${job.title} ${job.normalized_title} ${job.company} ${job.job_description}`.toLowerCase();
    const place = `${job.location} ${job.city}`.toLowerCase();
    return (!normalizedQuery || searchable.includes(normalizedQuery)) && (!normalizedLocation || place.includes(normalizedLocation));
  });
  return {
    hits: filtered.slice(offset, offset + limit),
    estimatedTotalHits: filtered.length,
    nbHits: filtered.length,
    limit,
    offset,
    query,
    market: "IN",
    demo: true,
  };
}
